// components/features/support/tasks/TaskDetailModal.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
  X,
  Calendar,
  AlertCircle,
  Paperclip,
  CheckCircle2,
  Edit,
  Trash2,
  Play,
  Pause,
  RotateCcw,
  Clock,
  Loader2,
} from "lucide-react";
import { Task, TaskPriority, TaskStatus } from "../../../types/Task";
import { taskService } from "../../../services/taskService";
import useToken from "../../../../../hooks/auth/useToken";

interface TaskDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  taskId: string | null;
  currentUserId?: string | null;
  onEdit?: (task: Task) => void;
  onDelete?: (task: Task) => void;
  onTaskUpdated?: () => void;
}

const statusLabels: Record<TaskStatus, string> = {
  nouveau: "Nouveau",
  en_cours: "En cours",
  en_attente: "En attente",
  resolu: "Résolu",
};

const statusStyles: Record<TaskStatus, string> = {
  nouveau: "bg-blue-100 text-blue-700",
  en_cours: "bg-purple-100 text-purple-700",
  en_attente: "bg-orange-100 text-orange-700",
  resolu: "bg-green-100 text-green-700",
};

const priorityLabels: Record<TaskPriority, string> = {
  basse: "Basse",
  moyenne: "Moyenne",
  haute: "Haute",
  critique: "Critique",
};

const priorityStyles: Record<TaskPriority, string> = {
  basse: "bg-gray-100 text-gray-600",
  moyenne: "bg-blue-100 text-blue-700",
  haute: "bg-orange-100 text-orange-700",
  critique: "bg-red-100 text-red-700",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `il y a ${days} j`;
  const months = Math.floor(days / 30);
  if (months < 12) return `il y a ${months} mois`;
  return `il y a ${Math.floor(months / 12)} an(s)`;
}

function deadlineLabel(dueStr: string, status: TaskStatus): { text: string; tone: "danger" | "warn" | "ok" } {
  const due = new Date(dueStr).getTime();
  const now = Date.now();
  const diff = due - now;
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));

  if (status === "resolu") {
    return {
      text: `Échéance : ${new Date(dueStr).toLocaleDateString("fr-FR")}`,
      tone: "ok",
    };
  }
  if (diff < 0) {
    const overdue = Math.abs(days);
    return { text: `En retard de ${overdue} j`, tone: "danger" };
  }
  if (days <= 1) return { text: "À faire aujourd'hui", tone: "danger" };
  if (days <= 3) return { text: `Dans ${days} jours`, tone: "warn" };
  return { text: `Dans ${days} jours`, tone: "ok" };
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  isOpen,
  onClose,
  taskId,
  currentUserId,
  onEdit,
  onDelete,
  onTaskUpdated,
}) => {
  const { token } = useToken();

  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTask = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await taskService.getTaskById(taskId, token);
      console.log(data);
      setTask(data);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement de la tâche");
    } finally {
      setLoading(false);
    }
  }, [taskId, token]);

  useEffect(() => {
    if (isOpen && taskId) {
      fetchTask();
    } else {
      setTask(null);
    }
  }, [isOpen, taskId, fetchTask]);

  const handleStatusChange = async (status: TaskStatus) => {
    if (!taskId) return;
    setActionLoading(true);
    try {
      await taskService.updateStatus(taskId, status, token);
      await fetchTask();
      onTaskUpdated?.();
    } catch (err) {
      console.error(err);
      setError("Impossible de changer le statut");
    } finally {
      setActionLoading(false);
    }
  };

  if (!isOpen) return null;

  if (loading || !task) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-12 flex flex-col items-center justify-center">
          {error ? (
            <>
              <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
              <p className="text-sm text-gray-600">{error}</p>
              <button onClick={onClose} className="mt-4 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Fermer
              </button>
            </>
          ) : (
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          )}
        </div>
      </div>
    );
  }

  const deadline = task.due_date ? deadlineLabel(task.due_date, task.status) : null;

  const isCreator = Boolean(currentUserId) && task.creator?.id === currentUserId;
  const canDelete = isCreator && Boolean(onDelete);

  const primaryAction = (() => {
    switch (task.status) {
      case "nouveau":
        return {
          label: "Prendre en charge",
          icon: Play,
          color: "bg-blue-600 hover:bg-blue-700",
          next: "en_cours" as TaskStatus,
        };
      case "en_cours":
        return {
          label: "Marquer comme résolu",
          icon: CheckCircle2,
          color: "bg-green-600 hover:bg-green-700",
          next: "resolu" as TaskStatus,
        };
      case "en_attente":
        return {
          label: "Reprendre",
          icon: RotateCcw,
          color: "bg-blue-600 hover:bg-blue-700",
          next: "en_cours" as TaskStatus,
        };
      default:
        return null;
    }
  })();

  const secondaryAction = (() => {
    if (task.status === "en_cours" || task.status === "nouveau") {
      return {
        label: "Mettre en attente",
        icon: Pause,
        next: "en_attente" as TaskStatus,
      };
    }
    return null;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* HEADER */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 leading-tight">{task.title}</h2>
              <div className="flex items-center gap-2 flex-wrap mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusStyles[task.status]}`}>
                  {statusLabels[task.status]}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${priorityStyles[task.priority]}`}>
                  {priorityLabels[task.priority]}
                </span>
                {deadline && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 font-medium ${
                      deadline.tone === "danger"
                        ? "bg-red-100 text-red-700"
                        : deadline.tone === "warn"
                          ? "bg-orange-100 text-orange-700"
                          : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <Clock className="w-3 h-3" />
                    {deadline.text}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="px-6 py-5 overflow-y-auto space-y-5">
          {task.due_date && task.status !== "resolu" && new Date(task.due_date) < new Date() && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Cette tâche est en retard — elle nécessite votre attention
            </div>
          )}

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Consigne</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{task.description || "—"}</p>
          </div>

          {/* ✅ Note — visible dès qu'elle existe, quel que soit le statut */}
          {task.note && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Note</h3>
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-amber-50 border border-amber-200 rounded-lg p-3">
                {task.note}
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Créé par</p>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                  {task.creator?.name?.charAt(0).toUpperCase() || "?"}
                </div>
                <div className="text-sm">
                  <p className="text-gray-800 font-medium">{task.creator?.name || "—"}</p>
                  <p className="text-xs text-gray-400">{timeAgo(task.created_at)}</p>
                </div>
              </div>
            </div>

            {task.client?.full_name && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Client concerné</p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                    {task.client.full_name.charAt(0).toUpperCase()}
                  </div>
                  <p className="text-sm text-gray-800 font-medium truncate">{task.client.full_name}</p>
                </div>
              </div>
            )}

            {task.due_date && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Échéance</p>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  {new Date(task.due_date).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            )}

            {task.completed_at && (
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Résolu le</p>
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  {new Date(task.completed_at).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Assigné à {task.agents?.length ? `(${task.agents.length})` : ""}
            </h3>
            {task.agents?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {task.agents.map((agent) => (
                  <div
                    key={agent.id}
                    className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5"
                  >
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                      {agent.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex flex-col leading-tight">
                      <span className="text-sm text-gray-800">{agent.name}</span>
                      <span className="text-xs text-gray-400">{agent.email}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Aucun admin assigné</p>
            )}
          </div>

          {task.attachments?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Paperclip className="w-3.5 h-3.5" />
                Pièces jointes ({task.attachments.length})
              </h3>
              <div className="flex flex-wrap gap-2">
                {task.attachments.map((url, index) => (
                  <a
                    key={index}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-sm transition-all"
                  >
                    <img src={url} alt={`Pièce jointe ${index + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          {task.status === "resolu" && (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">Cette tâche est terminée</span>
              </div>
              <div className="flex items-center gap-2">
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      onDelete?.(task);
                      onClose();
                    }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                )}
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      onEdit(task);
                      onClose();
                    }}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Modifier
                  </button>
                )}
              </div>
            </div>
          )}

          {task.status !== "resolu" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2 order-2 sm:order-1">
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      onDelete?.(task);
                      onClose();
                    }}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                )}
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      onEdit(task);
                      onClose();
                    }}
                    disabled={actionLoading}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    <Edit className="w-4 h-4" />
                    Modifier
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2 order-1 sm:order-2">
                {secondaryAction && (
                  <button
                    type="button"
                    onClick={() => handleStatusChange(secondaryAction.next)}
                    disabled={actionLoading}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <secondaryAction.icon className="w-4 h-4" />
                    )}
                    {secondaryAction.label}
                  </button>
                )}
                {primaryAction && (
                  <button
                    type="button"
                    onClick={() => handleStatusChange(primaryAction.next)}
                    disabled={actionLoading}
                    className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm text-white font-medium shadow-sm transition-colors disabled:opacity-50 ${primaryAction.color}`}
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <primaryAction.icon className="w-4 h-4" />
                    )}
                    {primaryAction.label}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
